import { useState } from 'react';
import type { ExportStructureNode, XmlAttribute } from '@capture/shared';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AttributeRow } from './AttributeRow';
import { reorderChildren } from './StructureBuilder';
import { TextPromptModal } from '@/components/TextPromptModal';
import styles from './export-composer.module.css';

/**
 * Mapping shape required by the StructureBuilder field picker. Carrying
 * both `targetColumn` and `sourceField` end-to-end is what fixes #59
 * (leaf nodes used to render `{targetColumn} ← {targetColumn}` because
 * handleAddField set both sides to the same string).
 */
export interface StructureMapping {
  targetColumn: string;
  sourceField: string;
}

interface Props {
  node: ExportStructureNode;
  depth: number;
  onUpdate: (updated: ExportStructureNode) => void;
  onDelete: () => void;
  mappings: StructureMapping[];
  /**
   * Full root structure so descendant pickers can compute the global
   * "used vs unused" set across the whole tree, not just the local node's
   * subtree. Without this every container would offer the same fields
   * (operator could place `vendor_name` once at root and again under
   * `header`).
   */
  rootStructure: ExportStructureNode;
  /** Controls whether the group/collection toggle is shown. JSON only — XML doesn't distinguish. */
  format: 'json' | 'xml';
  /**
   * F-031 — sortable id (the child's index, as a string) supplied by the parent's
   * SortableContext. When present, this node renders as a draggable sortable item
   * so siblings can be reordered. The root node never receives one.
   */
  sortableId?: string;
}

export function StructureNode({ node, depth, onUpdate, onDelete, mappings, rootStructure, format, sortableId }: Props) {
  const [editing, setEditing] = useState(false);
  const [groupPromptOpen, setGroupPromptOpen] = useState(false);
  const [groupPromptType, setGroupPromptType] = useState<'group' | 'collection'>('group');

  // F-031 — when this node is a sortable child, wire dnd-kit's sortable so the
  // drag handle reorders siblings. Hooks must run unconditionally; an absent
  // sortableId yields a stable disabled id.
  const sortable = useSortable({ id: sortableId ?? '__static__', disabled: sortableId === undefined });
  const sortableStyle = sortableId !== undefined
    ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }
    : undefined;

  // F-031 — reorder this node's children on drag-end. ids are stringified indices.
  const childSensors = useSensors(useSensor(PointerSensor));
  function handleChildDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = Number(active.id);
    const to = Number(over.id);
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    onUpdate({ ...node, children: reorderChildren(node.children ?? [], from, to) });
  }

  const isContainer = node.type === 'group' || node.type === 'collection';
  const canAddChildren = isContainer && depth < 5;

  function handleNameChange(name: string) {
    onUpdate({ ...node, name });
  }

  function handleTypeToggle() {
    if (node.type === 'group') {
      onUpdate({ ...node, type: 'collection' });
    } else if (node.type === 'collection') {
      onUpdate({ ...node, type: 'group' });
    }
  }

  function handleAddGroupNamed(name: string) {
    const newNode: ExportStructureNode = { name, type: groupPromptType, children: [] };
    onUpdate({ ...node, children: [...(node.children ?? []), newNode] });
    setGroupPromptOpen(false);
    setGroupPromptType('group'); // reset for next time
  }

  const existingSiblingGroupNames = new Set(
    (node.children ?? [])
      .filter((c) => c.type === 'group' || c.type === 'collection')
      .map((c) => c.name),
  );

  // #58 + #59 — picker over unused mappings; never silent on exhaust;
  // the chosen mapping carries its real sourceField onto the new field
  // node so the leaf renders `{targetColumn} ← {sourceField}` correctly.
  const usedTargets = collectUsedTargets(rootStructure);
  const unusedMappings = mappings.filter((m) => !usedTargets.has(m.targetColumn));

  function handlePickField(targetColumn: string) {
    const mapping = mappings.find((m) => m.targetColumn === targetColumn);
    if (!mapping) return;
    const newField: ExportStructureNode = {
      name: mapping.targetColumn,
      type: 'field',
      sourceField: mapping.sourceField,
    };
    onUpdate({ ...node, children: [...(node.children ?? []), newField] });
  }

  function handleChildUpdate(index: number, updated: ExportStructureNode) {
    const children = (node.children ?? []).map((c, i) => (i === index ? updated : c));
    onUpdate({ ...node, children });
  }

  function handleChildDelete(index: number) {
    // Move grandchildren up to this node's children (flatten one level)
    const deleted = node.children?.[index];
    const deletedChildren = deleted?.children ?? [];
    const remaining = (node.children ?? []).filter((_, i) => i !== index);
    onUpdate({ ...node, children: [...remaining, ...deletedChildren] });
  }

  const availableFieldNames = mappings.map((m) => m.sourceField);

  function handleAttributeUpdate(idx: number, next: XmlAttribute) {
    const attrs = [...(node.attributes ?? [])];
    attrs[idx] = next;
    onUpdate({ ...node, attributes: attrs });
  }

  function handleAttributeDelete(idx: number) {
    const attrs = [...(node.attributes ?? [])];
    attrs.splice(idx, 1);
    onUpdate({ ...node, attributes: attrs });
  }

  function handleAttributeAdd() {
    const attrs = [...(node.attributes ?? []), { name: '', source: 'literal' as const, literal: '' }];
    onUpdate({ ...node, attributes: attrs });
  }

  const nodeClass = [
    styles.structureNode,
    node.type === 'collection' ? styles.structureNodeCollection : '',
  ].filter(Boolean).join(' ');

  const childrenClass = node.type === 'collection'
    ? styles.structureChildrenCollection
    : styles.structureChildren;

  return (
    <div
      ref={sortableId !== undefined ? sortable.setNodeRef : undefined}
      style={{ marginLeft: depth > 0 ? 24 : 0, ...sortableStyle }}
    >
      <div className={nodeClass}>
        {/* Drag handle — F-031: carries sortable listeners when this node is a
            reorderable child. */}
        <span
          className={styles.structureDragHandle}
          aria-label="drag handle"
          {...(sortableId !== undefined ? { ...sortable.attributes, ...sortable.listeners } : {})}
        >
          ⠿
        </span>

        {/* Icon — type-aware in JSON mode, folder in XML mode */}
        {isContainer ? (
          <span style={{ fontSize: 13, fontFamily: 'monospace' }}>
            {format === 'json'
              ? (node.type === 'collection' ? '[ ]' : '{ }')
              : '📁'}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: '#6d28d9' }}>◆</span>
        )}

        {/* Name */}
        {editing && isContainer ? (
          <input
            type="text"
            className={styles.structureNodeNameInput}
            value={node.name}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={() => setEditing(false)}
            autoFocus
            aria-label={`Edit name: ${node.name}`}
          />
        ) : (
          <span
            className={isContainer ? styles.structureNodeNameContainer : styles.structureNodeName}
            onClick={() => isContainer && setEditing(true)}
          >
            {node.name}
          </span>
        )}

        {/* ARRAY badge for collections */}
        {node.type === 'collection' && (
          <span className={styles.structureBadge}>
            ARRAY
          </span>
        )}

        {/* Source field indicator for field nodes */}
        {node.type === 'field' && node.sourceField && (
          <span className={styles.structureNodeSourceField}>← {node.sourceField}</span>
        )}

        {/* Type toggle for group/collection — always visible on containers in JSON mode */}
        {isContainer && format === 'json' && (
          <button
            type="button"
            className={styles.structureTypeToggleBtn}
            onClick={handleTypeToggle}
            title={node.type === 'group'
              ? 'Currently object {}. Click to make array []. Applies to JSON output only.'
              : 'Currently array []. Click to make object {}. Applies to JSON output only.'}
          >
            {node.type === 'group' ? 'Make Array' : 'Make Group'}
          </button>
        )}

        {/* Delete button */}
        <button
          type="button"
          className={styles.structureNodeDeleteBtn}
          onClick={onDelete}
          aria-label={`Delete ${node.name}`}
        >
          ×
        </button>
      </div>

      {/* XML attributes collapsible block — hidden entirely in JSON mode */}
      {format === 'xml' && (
        <details className={styles.attributesBlock}>
          <summary>Attributes ({(node.attributes ?? []).length})</summary>
          {(node.attributes ?? []).map((attr, i) => (
            <AttributeRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              attr={attr}
              availableFields={availableFieldNames}
              onUpdate={(next) => handleAttributeUpdate(i, next)}
              onDelete={() => handleAttributeDelete(i)}
            />
          ))}
          <button type="button" aria-label="+ Add attribute" onClick={handleAttributeAdd}>
            + Add attribute
          </button>
        </details>
      )}

      {/* Children — F-031: draggable/sortable so siblings can be reordered. */}
      {isContainer && node.children && node.children.length > 0 && (
        <DndContext
          sensors={childSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleChildDragEnd}
        >
          <SortableContext
            items={node.children.map((_, i) => String(i))}
            strategy={verticalListSortingStrategy}
          >
            <div className={childrenClass}>
              {node.children.map((child, i) => (
                <StructureNode
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  sortableId={String(i)}
                  node={child}
                  depth={depth + 1}
                  onUpdate={(updated) => handleChildUpdate(i, updated)}
                  onDelete={() => handleChildDelete(i)}
                  mappings={mappings}
                  rootStructure={rootStructure}
                  format={format}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add affordances (only for containers within max depth) */}
      {canAddChildren && (
        <div className={styles.structureAddRow} style={{ marginLeft: depth > 0 ? 24 : 0 }}>
          {format === 'json' ? (
            <>
              <button
                type="button"
                className={styles.structureAddChildBtn}
                onClick={() => { setGroupPromptType('group'); setGroupPromptOpen(true); }}
                aria-label="+ Object"
              >
                + Object
              </button>
              <button
                type="button"
                className={styles.structureAddChildBtn}
                onClick={() => { setGroupPromptType('collection'); setGroupPromptOpen(true); }}
                aria-label="+ Array"
              >
                + Array
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.structureAddChildBtn}
              onClick={() => { setGroupPromptType('group'); setGroupPromptOpen(true); }}
              aria-label="+ Group"
            >
              + Group
            </button>
          )}
          {/* #58 — picker over unused mappings; never silent. */}
          <select
            className={styles.structureAddChildBtn}
            aria-label="Add a field from a mapping"
            value=""
            disabled={unusedMappings.length === 0}
            onChange={(e) => {
              if (e.target.value) handlePickField(e.target.value);
            }}
          >
            <option value="">
              {unusedMappings.length === 0 ? 'All fields placed' : '+ Add field…'}
            </option>
            {unusedMappings.map((m) => (
              <option key={m.targetColumn} value={m.targetColumn}>
                {m.targetColumn}
              </option>
            ))}
          </select>
        </div>
      )}
      <TextPromptModal
        open={groupPromptOpen}
        title={groupPromptType === 'collection' ? 'Add array' : 'Add group'}
        description={
          groupPromptType === 'collection'
            ? 'Arrays let you group repeated items under a parent element in the exported document.'
            : 'Groups let you nest fields under a parent element in the exported document.'
        }
        placeholder="e.g. Address"
        submitLabel={groupPromptType === 'collection' ? 'Add array' : 'Add group'}
        validate={(name) =>
          existingSiblingGroupNames.has(name)
            ? `A group named "${name}" already exists at this level`
            : null
        }
        onConfirm={handleAddGroupNamed}
        onCancel={() => setGroupPromptOpen(false)}
      />
    </div>
  );
}

/**
 * Collect every targetColumn already placed somewhere in the tree. Keyed
 * by the field-node `name` (which is the targetColumn — see #59 fix in
 * handlePickField). Used by descendant pickers to gate their options
 * globally rather than per-subtree, so the same mapping can't be added
 * twice across siblings.
 */
function collectUsedTargets(node: ExportStructureNode): Set<string> {
  const used = new Set<string>();
  function walk(n: ExportStructureNode) {
    if (n.type === 'field') used.add(n.name);
    n.children?.forEach(walk);
  }
  walk(node);
  return used;
}
